import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorMode,
  useToast,
  Spinner,
  Badge,
  Icon,
  Select,
  FormErrorMessage,
  InputGroup,
  InputLeftAddon,
  Avatar,
} from '@chakra-ui/react';
import { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiCheck } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { SunshineBackground } from './SunshineBackground';
import { getColor } from '../../../brandConfig';
import {
  formatPhoneNumber,
  toInternationalFormat,
  isValidPhoneNumber,
  getPhoneValidationError,
  COUNTRY_CODES,
  type CountryCode,
} from '../../authentication';
import {
  PUBLIC_CALENDAR_CONFIRM_QUERY as PUBLIC_CALENDAR_QUERY,
  CREATE_PUBLIC_BOOKING,
} from '../queries';

// ==================== TYPES ====================

interface LocationState {
  eventTypeId: string;
  eventTypeName?: string;
  startTime: string;
  endTime: string;
  calendarId?: string;
  calendarName?: string;
}

// ==================== COMPONENT ====================

export const PublicBookingConfirmPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const state = location.state as LocationState;

  // Form state
  const [bookerName, setBookerName] = useState('');
  const [bookerEmail, setBookerEmail] = useState('');
  const [bookerPhone, setBookerPhone] = useState(''); // International format for backend
  const [localPhoneNumber, setLocalPhoneNumber] = useState(''); // Display format for user
  const [notes, setNotes] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    COUNTRY_CODES.find(c => c.code === '+61') || COUNTRY_CODES[0]
  );
  const [phoneError, setPhoneError] = useState<string>('');

  const { colorMode } = useColorMode();

  // Fetch calendar data
  const { data: calendarData } = useQuery(PUBLIC_CALENDAR_QUERY, {
    variables: { slug },
    skip: !slug,
  });

  const calendar = calendarData?.publicCalendar;

  // Use brandConfig colors
  const bg = getColor("background.main", colorMode);
  const cardGradientBg = colorMode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 248, 248, 0.65) 50%, rgba(255, 255, 255, 0.7) 100%)'
    : 'linear-gradient(135deg, rgba(20, 20, 20, 0.7) 0%, rgba(30, 30, 30, 0.5) 50%, rgba(20, 20, 20, 0.7) 100%)';
  const cardBorder = colorMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  const textPrimary = getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode);

  // Booking Details card colors (brandConfig-aware)
  const bookingDetailsBg = colorMode === 'light'
    ? 'linear-gradient(135deg, rgba(239, 246, 255, 0.8) 0%, rgba(229, 236, 255, 0.7) 100%)' // Light blue gradient
    : 'linear-gradient(135deg, rgba(30, 58, 90, 0.8) 0%, rgba(45, 55, 72, 0.7) 100%)'; // Dark blue gradient
  const bookingDetailsBorder = colorMode === 'light' ? getColor('primary', colorMode) : '#4299e1';
  const bookingDetailsTextColor = colorMode === 'light' ? textPrimary : 'gray.100';

  // Mutation
  const [createBooking, { loading: creating }] = useMutation(CREATE_PUBLIC_BOOKING, {
    onCompleted: (data) => {
      if (data.createPublicBooking.requiresPayment && data.createPublicBooking.stripeCheckoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = data.createPublicBooking.stripeCheckoutUrl;
      } else {
        // Show success message
        toast({
          title: 'Booking Confirmed!',
          description: 'Your appointment has been scheduled. Check your email for confirmation.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Navigate to success page
        navigate(`/book/${slug}/success`, {
          state: {
            booking: data.createPublicBooking.booking,
            bookingToken: data.createPublicBooking.bookingToken,
          },
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Booking Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // ==================== HANDLERS ====================

  const handlePhoneInput = (value: string) => {
    const formatted = formatPhoneNumber(value, selectedCountry.code);
    setLocalPhoneNumber(formatted);

    // Store the full international number for backend
    const international = toInternationalFormat(formatted, selectedCountry.code);
    setBookerPhone(international);

    // Clear error when user types
    if (phoneError) setPhoneError('');
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (country) {
      setSelectedCountry(country);
      // Reformat existing number for new country
      if (localPhoneNumber) {
        handlePhoneInput(localPhoneNumber);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state?.eventTypeId || !state?.startTime) {
      toast({
        title: 'Missing Information',
        description: 'Please select a time slot first.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate(`/book/${slug}`);
      return;
    }

    // Validate phone number if provided
    if (localPhoneNumber && !isValidPhoneNumber(localPhoneNumber, selectedCountry)) {
      const error = getPhoneValidationError(localPhoneNumber, selectedCountry);
      setPhoneError(error);
      toast({
        title: 'Invalid Phone Number',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await createBooking({
        variables: {
          input: {
            calendarSlug: slug,
            eventTypeId: state.eventTypeId,
            startTime: state.startTime,
            bookerName,
            bookerEmail,
            bookerPhone, // Send international format to backend
            bookerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            answers: notes ? { notes } : {},
          },
        },
      });
    } catch (err) {
      // Error handled by onError callback
      console.error('Booking error:', err);
    }
  };

  // ==================== VALIDATION ====================

  if (!state?.eventTypeId || !state?.startTime) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          <Box>
            <AlertTitle>Invalid Booking Link</AlertTitle>
            <AlertDescription>
              Please start from the beginning and select a time slot.
            </AlertDescription>
          </Box>
        </Alert>
        <Button mt={4} onClick={() => navigate(`/book/${slug}`)}>
          Go to Booking Page
        </Button>
      </Container>
    );
  }

  // ==================== RENDER ====================

  return (
      <Box minH="100vh" position="relative" bg={bg}>
        <SunshineBackground />
        <Box position="relative" zIndex={1}>
      <Container maxW="container.md" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Card bg={cardGradientBg} backdropFilter="blur(10px)" borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack spacing={4} w="full">
                  {calendar?.bookingPageProfilePhoto && (
                    <Avatar
                      size="lg"
                      src={calendar.bookingPageProfilePhoto || calendar.bookingPageLogo}
                      name={calendar.bookingPageTitle || calendar.name}
                    />
                  )}
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                      <Icon as={FiCalendar} boxSize={6} color="blue.500" />
                      <Heading size="lg">Confirm Your Booking</Heading>
                    </HStack>
                    <Text color="gray.600">
                      Please provide your details to complete the booking.
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Booking Summary */}
          <Card bg={bookingDetailsBg} borderColor={bookingDetailsBorder} borderWidth="2px" backdropFilter="blur(10px)">
            <CardBody>
              <VStack align="start" spacing={3}>
                <Heading size="md" color={bookingDetailsTextColor}>Booking Details</Heading>
                <HStack>
                  <Icon as={FiCalendar} color={bookingDetailsTextColor} />
                  <Text fontWeight="medium" color={bookingDetailsTextColor}>
                    {format(parseISO(state.startTime), 'EEEE, MMMM d, yyyy')}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={FiClock} color={bookingDetailsTextColor} />
                  <Text fontWeight="medium" color={bookingDetailsTextColor}>
                    {format(parseISO(state.startTime), 'h:mm a')} -{' '}
                    {format(parseISO(state.endTime), 'h:mm a')}
                  </Text>
                </HStack>
                {state.eventTypeName && (
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                    {state.eventTypeName}
                  </Badge>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Booking Form */}
          <Card bg={cardGradientBg} backdropFilter="blur(10px)" borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>
                      <HStack>
                        <Icon as={FiUser} />
                        <Text>Full Name</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="text"
                      value={bookerName}
                      onChange={(e) => setBookerName(e.target.value)}
                      placeholder="John Smith"
                      size="lg"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <HStack>
                        <Icon as={FiMail} />
                        <Text>Email Address</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="email"
                      value={bookerEmail}
                      onChange={(e) => setBookerEmail(e.target.value)}
                      placeholder="john@example.com"
                      size="lg"
                    />
                  </FormControl>

                  <FormControl isInvalid={!!phoneError}>
                    <FormLabel>
                      <HStack>
                        <Icon as={FiPhone} />
                        <Text>Phone Number</Text>
                      </HStack>
                    </FormLabel>
                    <VStack align="stretch" spacing={2}>
                      <Select
                        value={selectedCountry.code}
                        onChange={handleCountryChange}
                        size="lg"
                      >
                        {COUNTRY_CODES.map((country) => (
                          <option key={country.code + country.country} value={country.code}>
                            {country.flag} {country.country} ({country.code})
                          </option>
                        ))}
                      </Select>
                      <InputGroup size="lg">
                        <InputLeftAddon>{selectedCountry.code}</InputLeftAddon>
                        <Input
                          type="tel"
                          value={localPhoneNumber}
                          onChange={(e) => handlePhoneInput(e.target.value)}
                          placeholder={selectedCountry.placeholder}
                        />
                      </InputGroup>
                    </VStack>
                    {localPhoneNumber && (
                      <Text fontSize="xs" color={textSecondary} mt={1}>
                        Will be sent as: {bookerPhone}
                      </Text>
                    )}
                    {phoneError && <FormErrorMessage>{phoneError}</FormErrorMessage>}
                    <Text fontSize="xs" color={textSecondary} mt={phoneError ? 1 : 0}>
                      For SMS reminders about your booking
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Additional Notes</FormLabel>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything else you'd like us to know..."
                      rows={4}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width="full"
                    leftIcon={creating ? <Spinner size="sm" /> : <Icon as={FiCheck} />}
                    isLoading={creating}
                    mt={4}
                  >
                    Confirm Booking
                  </Button>

                  <Button
                    variant="ghost"
                    size="md"
                    width="full"
                    onClick={() => navigate(-1)}
                    isDisabled={creating}
                  >
                    Go Back
                  </Button>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </VStack>
      </Container>
        </Box>
      </Box>
  );
};

export default PublicBookingConfirmPage;
